"use client"

import { useRouter } from "next/navigation"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import type { ProjetoPlanejamento } from "./types"

export const PROJETO_COOKIE = "__projeto_ativo"
export const PROJETO_LOCALSTORAGE_KEY = "compass:projeto-ativo"
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export type ProjetoPlanejamentoContextValue = {
  projetoAtivo: ProjetoPlanejamento | null
  projetos: readonly ProjetoPlanejamento[]
  setProjetoAtivo: (id: string) => void
}

const ProjetoPlanejamentoContext =
  createContext<ProjetoPlanejamentoContextValue | null>(null)

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const target = `${name}=`
  for (const raw of document.cookie.split(";")) {
    const trimmed = raw.trim()
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length))
    }
  }
  return null
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return
  const secure =
    typeof window !== "undefined" && window.location?.protocol === "https:"
      ? "; secure"
      : ""
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`
}

type ProviderProps = {
  initialProjetos: readonly ProjetoPlanejamento[]
  initialProjetoId: string | null
  children: ReactNode
}

export function ProjetoPlanejamentoProvider({
  initialProjetos,
  initialProjetoId,
  children,
}: ProviderProps) {
  const router = useRouter()

  const projetoById = useMemo(() => {
    const map = new Map<string, ProjetoPlanejamento>()
    for (const p of initialProjetos) map.set(p.id, p)
    return map
  }, [initialProjetos])

  const [ativoId, setAtivoId] = useState<string | null>(() => {
    if (initialProjetoId && projetoById.has(initialProjetoId)) {
      return initialProjetoId
    }
    return null
  })

  // Hidratacao client-side: localStorage tem precedencia, depois cookie.
  // Ambos so vencem se o id existir na lista entregue pelo servidor.
  useEffect(() => {
    let next: string | null = null
    try {
      const fromStorage = window.localStorage.getItem(PROJETO_LOCALSTORAGE_KEY)
      if (fromStorage && projetoById.has(fromStorage)) {
        next = fromStorage
      }
    } catch {
      // localStorage indisponivel (modo privado/SSR): ignora silenciosamente.
    }
    if (next === null) {
      const fromCookie = readCookie(PROJETO_COOKIE)
      if (fromCookie && projetoById.has(fromCookie)) {
        next = fromCookie
      }
    }
    if (next !== null && next !== ativoId) {
      // Hidratacao one-shot a partir de storage/cookie depois que o cliente
      // monta — necessario porque window so existe client-side.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAtivoId(next)
    }
    // Roda apenas no mount; deps fixos (initial vem do server e nao muda em
    // runtime do Provider). Trocar projeto durante a sessao passa pelo setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setProjetoAtivo = useCallback(
    (id: string) => {
      if (!projetoById.has(id)) return
      setAtivoId(id)
      try {
        window.localStorage.setItem(PROJETO_LOCALSTORAGE_KEY, id)
      } catch {
        // localStorage indisponivel: cookie continua sendo fonte de verdade SSR.
      }
      writeCookie(PROJETO_COOKIE, id)
      router.refresh()
    },
    [projetoById, router],
  )

  const value = useMemo<ProjetoPlanejamentoContextValue>(
    () => ({
      projetoAtivo: ativoId ? (projetoById.get(ativoId) ?? null) : null,
      projetos: initialProjetos,
      setProjetoAtivo,
    }),
    [ativoId, projetoById, initialProjetos, setProjetoAtivo],
  )

  return (
    <ProjetoPlanejamentoContext.Provider value={value}>
      {children}
    </ProjetoPlanejamentoContext.Provider>
  )
}

export function useProjetoAtivo(): ProjetoPlanejamentoContextValue {
  const ctx = useContext(ProjetoPlanejamentoContext)
  if (!ctx) {
    throw new Error(
      "useProjetoAtivo deve ser usado dentro de <ProjetoPlanejamentoProvider>.",
    )
  }
  return ctx
}
